@if (visible) {
<div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white p-6 rounded-lg shadow-lg w-[90vw] max-w-[80%] max-h-[90vh] flex flex-col">

        <!-- ==================== Header ==================== -->
        <h2 class="text-lg font-bold mb-4">
            <i [class]="config.modalIcon + ' mr-2'"></i>
            {{ config.modalTitle | translate }}
        </h2>

        <!-- ==================== Tabs ==================== -->
        <div class="flex border-b border-gray-200 mb-4 overflow-x-auto">
            <!-- General tab (description + extras + default language) -->
            <button (click)="activeTab.set('general')"
                class="px-4 py-2 text-xs font-medium transition-colors whitespace-nowrap"
                [class.border-b-2]="activeTab() === 'general'"
                [class.border-[#009F9E]]="activeTab() === 'general'"
                [class.text-[#009F9E]]="activeTab() === 'general'"
                [class.text-gray-500]="activeTab() !== 'general'">
                <i class="fas fa-cog mr-1"></i> {{ 'General' | translate }}
                @if (getDefaultLanguage(); as defLang) {
                <span class="ml-1 text-[10px] opacity-70">({{ defLang.value }})</span>
                }
            </button>
            <!-- One tab per other language -->
            @for (lang of getOtherLanguages(); track lang.id) {
            <button (click)="activeTab.set(lang.id)"
                class="px-4 py-2 text-xs font-medium transition-colors whitespace-nowrap"
                [class.border-b-2]="activeTab() === lang.id"
                [class.border-[#009F9E]]="activeTab() === lang.id"
                [class.text-[#009F9E]]="activeTab() === lang.id"
                [class.text-gray-500]="activeTab() !== lang.id">
                <i class="fas fa-language mr-1"></i> {{ lang.value }}
            </button>
            }
        </div>

        <!-- ==================== Tab Content ==================== -->
        <div class="flex-1 overflow-y-auto">

            <!-- ========== GENERAL TAB (description + extras + default language) ========== -->
            @if (activeTab() === 'general') {
            <div class="space-y-3">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 text-xs">
                        <thead class="bg-gray-50 sticky top-0 z-10">
                            <tr>
                                <!-- Action column -->
                                @if (!config.singleItem) {
                                <th class="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider w-[60px]">
                                    {{ 'vues.translatableEntityModal.action' | translate }}
                                </th>
                                }
                                <!-- Description column -->
                                @if (!config.hideDescription) {
                                <th class="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                                    {{ 'vues.translatableEntityModal.description' | translate }}
                                </th>
                                }
                                <!-- Extra fields columns -->
                                @if (config.extraFields) {
                                @for (field of config.extraFields; track field.property) {
                                <th class="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                                    {{ field.label | translate }}
                                </th>
                                }
                                }
                                <!-- Default language translation fields -->
                                @if (getDefaultLanguage(); as defLang) {
                                @for (field of config.translationFields; track field.type) {
                                <th class="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                                    {{ field.label | translate }} ({{ defLang.value }})
                                </th>
                                }
                                }
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            @for (item of editableItems(); track trackById($index, item)) {
                            <tr class="hover:bg-gray-50">
                                <!-- Action -->
                                @if (!config.singleItem) {
                                <td class="px-2 py-2 whitespace-nowrap align-top">
                                    @if (isEditMode) {
                                    <div class="flex items-center gap-1">
                                        @if (config.childConfig) {
                                        <button (click)="toggleExpand(item.id)" class="text-gray-500 hover:text-[#009F9E]" title="Toggle answers">
                                            <i class="fas text-xs" [class.fa-chevron-down]="!isExpanded(item.id)" [class.fa-chevron-up]="isExpanded(item.id)"></i>
                                        </button>
                                        }
                                        <button (click)="removeItem(item.id)" class="text-red-500 hover:text-red-700" title="Delete">
                                            <i class="fas fa-times text-xs"></i>
                                        </button>
                                    </div>
                                    }
                                </td>
                                }

                                <!-- Description -->
                                @if (!config.hideDescription) {
                                <td class="px-2 py-2 align-top">
                                    @if (isEditMode) {
                                    <input type="text" [value]="item.description || ''"
                                        (input)="updateItemDescription(item.id, $any($event.target).value)"
                                        class="w-full border border-gray-300 p-1.5 text-xs rounded focus:ring-[#009F9E] focus:border-[#009F9E]"
                                        [placeholder]="'vues.translatableEntityModal.placeholders.description' | translate">
                                    } @else {
                                    <span class="text-xs">{{ item.description || '—' }}</span>
                                    }
                                </td>
                                }

                                <!-- Extra fields cells -->
                                @for (extra of config.extraFields || []; track extra.property) {
                                <td class="px-2 py-2 align-top">
                                    @if (isEditMode) {
                                    @if (extra.inputType === 'checkbox') {
                                    <input type="checkbox" class="w-4 h-4 mt-1"
                                        [checked]="!!item[extra.property]"
                                        (change)="updateItemExtra(item.id, extra.property, $any($event.target).checked)">
                                    }
                                    @if (extra.inputType === 'select' && extra.selectSource === 'self') {
                                    <select class="w-full border border-gray-300 p-1.5 text-xs rounded focus:ring-[#009F9E] focus:border-[#009F9E]"
                                        [value]="item[extra.property] ?? ''"
                                        (change)="updateItemExtra(item.id, extra.property, +$any($event.target).value || null)">
                                        <option value="">—</option>
                                        @for (other of editableItems(); track other.id) {
                                        @if (other.id !== item.id) {
                                        <option [value]="other.id" [selected]="item[extra.property] === other.id">
                                            {{ other.numero || other.id }} - {{ other.description || '' }}
                                        </option>
                                        }
                                        }
                                    </select>
                                    }
                                    @if (extra.inputType === 'radio-bool') {
                                    <div class="flex items-center gap-3 text-xs mt-1">
                                        <label class="flex items-center gap-1 cursor-pointer">
                                            <input type="radio" [name]="'extra_' + extra.property + '_' + item.id"
                                                [checked]="item[extra.property] === true"
                                                (change)="updateItemExtra(item.id, extra.property, true)" class="w-3 h-3"> {{ 'buttons.yes' | translate }}
                                        </label>
                                        <label class="flex items-center gap-1 cursor-pointer">
                                            <input type="radio" [name]="'extra_' + extra.property + '_' + item.id"
                                                [checked]="item[extra.property] === false"
                                                (change)="updateItemExtra(item.id, extra.property, false)" class="w-3 h-3"> {{ 'buttons.no' | translate }}
                                        </label>
                                    </div>
                                    }
                                    } @else {
                                    @if (extra.inputType === 'checkbox') {
                                    @if (item[extra.property]) { <i class="fas fa-check text-green-500"></i> }
                                    @else { <i class="fas fa-times text-gray-300"></i> }
                                    }
                                    @if (extra.inputType === 'radio-bool') {
                                    <span class="text-xs">{{ item[extra.property] === true ? 'Oui' : item[extra.property] === false ? 'Non' : '—' }}</span>
                                    }
                                    @if (extra.inputType === 'select' && extra.selectSource === 'self') {
                                    <span class="text-xs">{{ item[extra.property] || '—' }}</span>
                                    }
                                    }
                                </td>
                                }

                                <!-- Default language translation fields -->
                                @if (getDefaultLanguage(); as defLang) {
                                @for (field of config.translationFields; track field.type) {
                                <td class="px-2 py-2 align-top">
                                    @if (isEditMode) {
                                    @if (field.inputType === 'text') {
                                    <input type="text"
                                        [value]="getTranslation(item.id, defLang.id, field.type)"
                                        (input)="setTranslation(item.id, defLang.id, field.type, $any($event.target).value)"
                                        class="w-full border border-gray-300 p-1.5 text-xs rounded focus:ring-[#009F9E] focus:border-[#009F9E]"
                                        [placeholder]="(field.label | translate) + '...'">
                                    }
                                    @if (field.inputType === 'textarea') {
                                    <textarea rows="3"
                                        [value]="getTranslation(item.id, defLang.id, field.type)"
                                        (input)="setTranslation(item.id, defLang.id, field.type, $any($event.target).value)"
                                        class="w-full border border-gray-300 p-1.5 text-xs rounded focus:ring-[#009F9E] focus:border-[#009F9E] resize-y min-h-[60px]"
                                        [placeholder]="(field.label | translate) + '...'">
                                    </textarea>
                                    }
                                    @if (field.inputType === 'html') {
                                    <app-html-editor
                                        [template]="getTranslation(item.id, defLang.id, field.type)"
                                        (templateChange)="setTranslation(item.id, defLang.id, field.type, $event)">
                                    </app-html-editor>
                                    }
                                    } @else {
                                    @if (field.inputType === 'html') {
                                    <div class="text-xs prose prose-sm max-w-none"
                                        [innerHTML]="getTranslation(item.id, defLang.id, field.type)">
                                    </div>
                                    } @else {
                                    <span class="text-xs">{{ getTranslation(item.id, defLang.id, field.type) || '—' }}</span>
                                    }
                                    }
                                </td>
                                }
                                }
                            </tr>

                            <!-- ====== CHILD ROW (Answers) ====== -->
                            @if (config.childConfig && isExpanded(item.id)) {
                            <tr class="bg-gray-50">
                                <td [attr.colspan]="totalCols()" class="p-3">
                                    <div class="text-xs font-medium text-gray-600 mb-2">
                                        <i class="fas fa-list-ul mr-1"></i>{{ config.childConfig.entityLabel | translate }}
                                        <span class="text-gray-400">({{ (item.children || []).length }})</span>
                                    </div>

                                    <table class="min-w-full text-xs border border-gray-200 bg-white">
                                        <thead class="bg-gray-100">
                                            <tr>
                                                <th class="px-2 py-1.5 text-left font-medium text-gray-500 w-[40px]"></th>
                                                <th class="px-2 py-1.5 text-left font-medium text-gray-500">{{ 'vues.translatableEntityModal.description' | translate }}</th>
                                                @if (getDefaultLanguage(); as defLang) {
                                                @for (field of config.childConfig.translationFields; track field.type) {
                                                <th class="px-2 py-1.5 text-left font-medium text-gray-500">{{ field.label | translate }} ({{ defLang.value }})</th>
                                                }
                                                @if (config.childConfig.showRightResponse) {
                                                <th class="px-2 py-1.5 text-center font-medium text-gray-500 w-[60px]">
                                                    <i class="fas fa-check-circle"></i>
                                                </th>
                                                }
                                                }
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-100">
                                            @for (child of (item.children || []); track trackByChildId($index, child)) {
                                            <tr>
                                                <td class="px-2 py-1.5">
                                                    @if (isEditMode) {
                                                    <button (click)="removeChildItem(item.id, child.id)"
                                                        class="text-red-500 hover:text-red-700" title="Delete answer">
                                                        <i class="fas fa-times text-xs"></i>
                                                    </button>
                                                    }
                                                </td>
                                                <td class="px-2 py-1.5">
                                                    @if (isEditMode) {
                                                    <input type="text" [value]="child.description || ''"
                                                        (input)="updateChildDescription(item.id, child.id, $any($event.target).value)"
                                                        class="w-full border border-gray-300 p-1 text-xs rounded focus:ring-[#009F9E] focus:border-[#009F9E]"
                                                        [placeholder]="'vues.translatableEntityModal.placeholders.answer' | translate">
                                                    } @else {
                                                    <span>{{ child.description || '—' }}</span>
                                                    }
                                                </td>
                                                @if (getDefaultLanguage(); as defLang) {
                                                @for (field of config.childConfig.translationFields; track field.type) {
                                                <td class="px-2 py-1.5">
                                                    @if (isEditMode) {
                                                    <input type="text"
                                                        [value]="getChildTranslation(item.id, child.id, defLang.id, field.type)"
                                                        (input)="setChildTranslation(item.id, child.id, defLang.id, field.type, $any($event.target).value)"
                                                        class="w-full border border-gray-300 p-1 text-xs rounded focus:ring-[#009F9E] focus:border-[#009F9E]"
                                                        [placeholder]="(field.label | translate) + '...'">
                                                    } @else {
                                                    <span>{{ getChildTranslation(item.id, child.id, defLang.id, field.type) || '—' }}</span>
                                                    }
                                                </td>
                                                }
                                                @if (config.childConfig.showRightResponse) {
                                                <td class="px-2 py-1.5 text-center">
                                                    @if (isEditMode) {
                                                    <input type="checkbox" class="w-3.5 h-3.5"
                                                        [checked]="getChildRightResponse(child.id)"
                                                        (change)="setChildRightResponse(child.id, $any($event.target).checked)">
                                                    } @else {
                                                    @if (getChildRightResponse(child.id)) {
                                                    <i class="fas fa-check text-green-500 text-xs"></i>
                                                    }
                                                    }
                                                </td>
                                                }
                                                }
                                            </tr>
                                            }
                                            @if ((item.children || []).length === 0) {
                                            <tr>
                                                <td [attr.colspan]="2 + (config.childConfig.translationFields?.length || 0) + (config.childConfig.showRightResponse ? 1 : 0)"
                                                    class="px-4 py-2 text-gray-400 italic text-center">
                                                    {{ 'vues.translatableEntityModal.noAnswers' | translate }}
                                                </td>
                                            </tr>
                                            }
                                        </tbody>
                                    </table>

                                    @if (isEditMode) {
                                    <button (click)="addChildItem(item.id)"
                                        class="mt-2 bg-[#009F9E] text-white px-2 py-1 hover:bg-[#008a8a] text-xs rounded-[2px]">
                                        <i class="fas fa-plus mr-1"></i> {{ config.childConfig.addLabel | translate }}
                                    </button>
                                    }
                                </td>
                            </tr>
                            }
                            }

                            @if (editableItems().length === 0) {
                            <tr>
                                <td [attr.colspan]="totalCols()"
                                    class="px-4 py-3 text-xs text-gray-400 italic text-center">
                                    {{ 'vues.translatableEntityModal.noItems' | translate }}
                                </td>
                            </tr>
                            }
                        </tbody>
                    </table>
                </div>

                <!-- Add item button -->
                @if (isEditMode && !config.singleItem) {
                <button (click)="addItem()"
                    class="mt-2 bg-[#009F9E] text-white px-3 py-1.5 hover:bg-[#008a8a] text-xs rounded-[2px]">
                    <i class="fas fa-plus mr-1"></i> {{ config.addItemLabel | translate }}
                </button>
                }
            </div>
            }

            <!-- ========== LANGUAGE TABS (other languages) ========== -->
            @for (lang of getOtherLanguages(); track lang.id) {
            @if (activeTab() === lang.id) {
            <div class="space-y-3">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 text-xs">
                        <thead class="bg-gray-50 sticky top-0 z-10">
                            <tr>
                                <!-- Description (read-only reference) -->
                                @if (!config.hideDescription) {
                                <th class="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                                    {{ 'vues.translatableEntityModal.description' | translate }}
                                </th>
                                }
                                <!-- Translation fields for this language -->
                                @for (field of config.translationFields; track field.type) {
                                <th class="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                                    {{ field.label | translate }}
                                </th>
                                }
                                @if (config.showRightResponse) {
                                <th class="px-2 py-2 text-center font-medium text-gray-500 uppercase tracking-wider w-[80px]">
                                    <i class="fas fa-check-circle mr-1"></i> {{ lang.value }}
                                </th>
                                }
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            @for (item of editableItems(); track trackById($index, item)) {
                            <tr class="hover:bg-gray-50">
                                <!-- Description (read-only) -->
                                @if (!config.hideDescription) {
                                <td class="px-2 py-2 align-top">
                                    <span class="text-xs text-gray-500">{{ item.description || '—' }}</span>
                                </td>
                                }

                                <!-- Translation fields -->
                                @for (field of config.translationFields; track field.type) {
                                <td class="px-2 py-2 align-top">
                                    @if (isEditMode) {
                                    @if (field.inputType === 'text') {
                                    <input type="text"
                                        [value]="getTranslation(item.id, lang.id, field.type)"
                                        (input)="setTranslation(item.id, lang.id, field.type, $any($event.target).value)"
                                        class="w-full border border-gray-300 p-1.5 text-xs rounded focus:ring-[#009F9E] focus:border-[#009F9E]"
                                        [placeholder]="(field.label | translate) + '...'">
                                    }
                                    @if (field.inputType === 'textarea') {
                                    <textarea rows="3"
                                        [value]="getTranslation(item.id, lang.id, field.type)"
                                        (input)="setTranslation(item.id, lang.id, field.type, $any($event.target).value)"
                                        class="w-full border border-gray-300 p-1.5 text-xs rounded focus:ring-[#009F9E] focus:border-[#009F9E] resize-y min-h-[60px]"
                                        [placeholder]="(field.label | translate) + '...'">
                                    </textarea>
                                    }
                                    @if (field.inputType === 'html') {
                                    <app-html-editor
                                        [template]="getTranslation(item.id, lang.id, field.type)"
                                        (templateChange)="setTranslation(item.id, lang.id, field.type, $event)">
                                    </app-html-editor>
                                    }
                                    } @else {
                                    @if (field.inputType === 'html') {
                                    <div class="text-xs prose prose-sm max-w-none"
                                        [innerHTML]="getTranslation(item.id, lang.id, field.type)">
                                    </div>
                                    } @else {
                                    <span class="text-xs">{{ getTranslation(item.id, lang.id, field.type) || '—' }}</span>
                                    }
                                    }
                                </td>
                                }

                            </tr>
                            @if (config.childConfig && isExpanded(item.id)) {
                            <tr class="bg-gray-50">
                                <td [attr.colspan]="(config.hideDescription ? 0 : 1) + config.translationFields.length + (config.showRightResponse ? 1 : 0)" class="p-3">
                                    <div class="text-xs font-medium text-gray-600 mb-2">
                                        <i class="fas fa-list-ul mr-1"></i> {{ config.childConfig.entityLabel | translate }} ({{ lang.value }})
                                    </div>

                                    <table class="min-w-full text-xs border border-gray-200 bg-white">
                                        <thead class="bg-gray-100">
                                            <tr>
                                                <th class="px-2 py-1.5 text-left font-medium text-gray-500">{{ 'vues.translatableEntityModal.description' | translate }}</th>
                                                @for (field of config.childConfig.translationFields; track field.type) {
                                                <th class="px-2 py-1.5 text-left font-medium text-gray-500">{{ field.label | translate }}</th>
                                                }
                                                @if (config.childConfig.showRightResponse) {
                                                <th class="px-2 py-1.5 text-center font-medium text-gray-500 w-[60px]">
                                                    <i class="fas fa-check-circle"></i>
                                                </th>
                                                }
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-100">
                                            @for (child of (item.children || []); track trackByChildId($index, child)) {
                                            <tr>
                                                <td class="px-2 py-1.5 text-gray-500">{{ child.description || '—' }}</td>
                                                @for (field of config.childConfig.translationFields; track field.type) {
                                                <td class="px-2 py-1.5">
                                                    @if (isEditMode) {
                                                    <input type="text"
                                                        [value]="getChildTranslation(item.id, child.id, lang.id, field.type)"
                                                        (input)="setChildTranslation(item.id, child.id, lang.id, field.type, $any($event.target).value)"
                                                        class="w-full border border-gray-300 p-1 text-xs rounded focus:ring-[#009F9E] focus:border-[#009F9E]"
                                                        [placeholder]="(field.label | translate) + '...'">
                                                    } @else {
                                                    <span>{{ getChildTranslation(item.id, child.id, lang.id, field.type) || '—' }}</span>
                                                    }
                                                </td>
                                                }
                                            </tr>
                                            }
                                            @if ((item.children || []).length === 0) {
                                            <tr>
                                                <td [attr.colspan]="1 + (config.childConfig.translationFields?.length || 0) + (config.childConfig.showRightResponse ? 1 : 0)"
                                                    class="px-4 py-2 text-gray-400 italic text-center">
                                                    {{ 'vues.translatableEntityModal.noAnswers' | translate }}
                                                </td>
                                            </tr>
                                            }
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                            }
                            }

                            @if (editableItems().length === 0) {
                            <tr>
                                <td [attr.colspan]="(config.hideDescription ? 0 : 1) + config.translationFields.length + (config.showRightResponse ? 1 : 0)" class="px-4 py-3 text-xs text-gray-400 italic text-center">
                                    {{ 'vues.translatableEntityModal.noItemsGeneral' | translate }}
                                </td>
                            </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            }
            }

        </div>

        <!-- ==================== Footer ==================== -->
        <div class="mt-4 pt-3 border-t border-gray-200 flex justify-end gap-2">
            @if (isEditMode) {
            <button (click)="onSave()"
                class="bg-[#009F9E] text-white px-4 py-2 hover:bg-[#008a8a] text-xs rounded-[2px]">
                <i class="fas fa-save mr-1"></i> {{ 'buttons.save' | translate }}
            </button>
            }
            <button (click)="onClose()" class="bg-red-500 text-white uppercase py-2 px-4 text-xs rounded-[2px] hover:bg-red-600">
                {{ 'buttons.close' | translate }}
            </button>
        </div>
    </div>
</div>
}